import type { Entity } from '@backstage/catalog-model';
import type { ComponentProps } from 'react';

import { stringifyEntityRef } from '@backstage/catalog-model';
import { notEmpty } from '@ipen-portal/plugin-common';
import { List } from '@mui/material';
import { tss } from 'tss-react/mui';

import { KeyRoleListItems } from './KeyRoleListItems';
import { RelationalKeyRoleListItems } from './RelationalKeyRoleListItems';

const useStyles = tss.create({
  list: {
    '& a': {
      textDecoration: 'none',
    },
    'width': '100%',
  },
});

type Props<E extends Entity> = ComponentProps<typeof KeyRoleListItems> & {
  relationalRoles?: Omit<
    ComponentProps<typeof RelationalKeyRoleListItems<E>>,
      'clickable' | 'entity'
  >[];
};

export const KeyRoleList = <E extends Entity>({
  clickable,
  entity,
  relationalRoles,
  ...rest
}: Props<E>) => {
  const { classes } = useStyles();

  const mainItem = (
    <KeyRoleListItems
      key={stringifyEntityRef(entity)}
      {...rest}
      clickable={clickable}
      entity={entity}
    />
  );

  const relationalItems = (relationalRoles ?? [])
    .map((relDef, index) => (
      <RelationalKeyRoleListItems
        // eslint-disable-next-line @eslint-react/no-array-index-key
        key={index}
        {...relDef}
        clickable={clickable}
        entity={entity}
      />
    ))
    .filter(notEmpty);

  const items = [mainItem, ...relationalItems].filter(notEmpty);

  if (!items.length) {
    return null;
  }

  return (
    <List aria-label="Key Roles" className={classes.list}>
      {items}
    </List>
  );
};
